import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';
import { getErrorMessage } from '@/lib/error-utils';

type VerifyType = 'signup' | 'recovery' | 'email_change' | 'magiclink';

// This screen resends through Supabase's own confirmation rail, so its copy
// and its recovery path are its own -- not copied from ConfirmSignup, which
// runs a separate, custom token flow for the same "confirm your email"
// moment (P-06). Both routes are live: send-email and invite-platform-admin
// build links to /verify-email, send-signup-confirmation builds links to
// /confirm-signup.

export default function VerifyEmail() {
  const { t } = useTranslation(['auth']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenHash = searchParams.get('token_hash');
  const type = searchParams.get('type') as VerifyType | null;

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [resendEmail, setResendEmail] = useState('');
  const [resendSending, setResendSending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const verify = useCallback(async () => {
    try {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash!,
        type: type!,
      });

      if (error) {
        throw error;
      }

      setStatus('success');

      // Redirect based on email action type
      const redirectPath = type === 'recovery' ? '/reset-password' : type === 'signup' ? '/onboarding' : '/dashboard';
      setTimeout(() => {
        navigate(redirectPath);
      }, 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('auth:verifyEmailErrorGeneric');
      console.error('Email verification error:', err);
      setStatus('error');
      setErrorMessage(message);
    }
  }, [tokenHash, type, t, navigate]);

  useEffect(() => {
    if (!tokenHash || !type) {
      setStatus('error');
      setErrorMessage(t('auth:invalidConfirmationLink'));
      return;
    }

    verify();
  }, [tokenHash, type, t, verify]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendSending(true);
    setResendError(null);
    try {
      if (type === 'recovery') {
        const { error } = await supabase.auth.resetPasswordForEmail(resendEmail, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
      } else if (type === 'magiclink') {
        const { error } = await supabase.auth.signInWithOtp({ email: resendEmail });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.resend({ type: (type ?? 'signup') as 'signup' | 'email_change', email: resendEmail });
        if (error) throw error;
      }
      setResendSent(true);
    } catch (err: unknown) {
      setResendError(getErrorMessage(err) || t('auth:unexpectedError'));
    } finally {
      setResendSending(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-6">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>{t('auth:verifyEmailLoading')}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>{t('auth:verifyEmailErrorTitle')}</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            {resendSent ? (
              <Alert className="border-green-500 bg-green-50 dark:border-green-700 dark:bg-green-950">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  {t('auth:resendConfirmationSent')}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {resendError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{resendError}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleResend} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="verify-resend-email">{t('auth:email')}</Label>
                    <Input
                      id="verify-resend-email"
                      type="email"
                      placeholder={t('auth:emailPlaceholder')}
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      required
                      disabled={resendSending}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={resendSending || !resendEmail}>
                    {resendSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auth:resendConfirmation')}
                  </Button>
                </form>
                <Button
                  onClick={() => navigate('/auth')}
                  variant="link"
                  className="w-full text-xs underline"
                >
                  {t('auth:goToLogin')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
          <CardTitle>{t('auth:verifyEmailSuccessTitle')}</CardTitle>
          <CardDescription>
            {type === 'recovery' ? t('auth:verifyEmailSuccessRecovery') : t('auth:verifyEmailSuccessDefault')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            onClick={() => navigate(type === 'recovery' ? '/reset-password' : type === 'signup' ? '/onboarding' : '/dashboard')}
          >
            {type === 'recovery' ? t('auth:goToResetPassword') : t('auth:goToDashboard')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
